/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { ROUTES_LIST } from "Router";
import Booleanize from "utils/Booleanize";
import openToast from "utils/openToast";
import sleep from "utils/sleep";

import useTryCatch from "hooks/useTryCatch";
import { logout, persistLoginSession } from "lib/v2/LoginAPI";
import { getAvatar } from "models/User";

import AuthContext, { initialUserState, IUser } from "./context";
import { useRole } from "state/Role/context";

const routeToRoleLogin = () => {
  return ROUTES_LIST.LOGIN?.routeMap?.base;
};

const getUserProfile = (user: IUser, role: string) => {
  let profileRoute = "";
  if (role === "admin") {
    const map = ROUTES_LIST?.ADMINISTRATION?.routeMap;
    profileRoute = map.admins.edit(user.id);
  }

  return profileRoute;
};

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { setPermissionsAndPages } = useRole();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(initialUserState);
  // assuming that the refresh function is called when the user enters the page
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const updateAdministrationAccess = (hasAccess: boolean) => {
    setUser((prev) => ({ ...prev, hasAdministrationAccess: hasAccess }));
  };

  const handleLogin = useCallback((payload: any, role: string) => {
    if (payload) {
      Sentry.setUser({
        id: payload.id,
        name: payload.name,
      });

      setUser({
        id: payload.id,
        name: payload.name,
        avatar: getAvatar(payload),
        role,
        profile: getUserProfile(payload, role),
        hasAdministrationAccess: payload.has_administration_access,
        firstName: payload.first_name,
      });

      // pass pages, permissions, and role to RoleContext
      setPermissionsAndPages(payload);
      setIsLoggedIn(true);
      localStorage.setItem("is_expired", "false");
    }

    setIsLoading(false);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      Sentry.setUser(null);
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      completeLogoutProcess();
    }
  }, []);

  const completeLogoutProcess = () => {
    const redirectTo = routeToRoleLogin();

    clearUserSession();
    setUser(initialUserState);

    navigateToLogin(redirectTo);
  };

  const clearUserSession = () => {
    localStorage.clear();
    setIsLoggedIn(false);
    setIsLoading(false);
  };

  const navigateToLogin = async (redirectTo: string) => {
    await sleep(400);
    window.location.replace(redirectTo);
  };

  // create type for handlePersistLogin that returns boolean

  let handlePersistLogin = async () => {
    // using `isLogged` to skip checking state of `isLoggedIn` because it won't be updated until the next render.
    let isLogged: boolean = false;
    setIsLoading(true);
    try {
      //@ts-ignore
      const { data, status } = await persistLoginSession();

      if (status === "FAILED") {
        throw new Error("Persist login failed");
      } else {
        isLogged = true;
        const { user, role } = data;
        // @ts-ignore
        handleLogin(user, role);
      }
    } catch {
      isLogged = false;
      // bring him back to login page

      if (pathname !== ROUTES_LIST.LOGIN.routeMap.base) {
        const dir = routeToRoleLogin();
        navigate(dir);
      }
    } finally {
      setIsLoading(false);
    }

    return isLogged;
  };

  handlePersistLogin = useCallback(handlePersistLogin, [handlePersistLogin]);

  let handleRefresh = useTryCatch(async () => {
    const isLogged = await handlePersistLogin();
    // using `isLogged` to skip checking state of `isLoggedIn` because it won't be updated until the next render.
    const { LOGIN, FORGET_PASSWORD } = ROUTES_LIST;
    if (
      !isLogged &&
      // pathname !== ROUTES_MAP.login.entity &&
      ![LOGIN?.routeMap?.base, FORGET_PASSWORD?.routeMap.base].includes(
        pathname,
      )
    ) {
      const dir = routeToRoleLogin();
      navigate(dir);
    }
  });

  handleRefresh = useCallback(handleRefresh, [handleRefresh]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const isExpired = Booleanize(localStorage.getItem("is_expired"));

    const isMagicLink =
      searchParams.get("magic-link") || pathname.includes("magic-link");

    if (isExpired) {
      localStorage.removeItem("is_expired");
      openToast("Session expired, please login again.");
    }

    const shouldRefresh =
      !isMagicLink && !window.location.href.includes("token");

    if (shouldRefresh) handleRefresh();

    if (token && !isExpired) return;

    setIsLoading(false);
    setIsLoggedIn(false);
  }, []);

  const value = {
    user,
    updateAdministrationAccess,
    isLoggedIn,
    isLoading,
    handleLogin,
    handleLogout,
  };

  return (
    <AuthContext.Provider value={value} test-id="xwBQ67_r">
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
